/** Minimal shape needed to build a property URL (kept structural so both the global
 *  Property type and the properties-page local type satisfy it). */
interface PropertyUrlInput {
    id: string;
    title?: string | null;
    location?: string | null;
    location_data?: { slug?: string | null } | null;
}

/** Slugify a string: lowercase, spaces/punctuation → single hyphens, trimmed. */
export function slugify(value: string): string {
    return (value || '')
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * Build a clean property detail URL of the form:
 *   /properties/{location-slug}/{title-slug}
 *
 * The UUID is no longer part of the URL — the detail page resolves the property
 * from its title slug (see getPropertyIdentifier / the /by-slug API). When a title
 * is missing we fall back to the id so the link still resolves.
 */
export function getPropertyHref(property: PropertyUrlInput): string {
    const locationSlug = slugify(property.location_data?.slug || property.location || 'dubai');
    const titleSlug = slugify(property.title || '');
    const lastSegment = titleSlug || property.id;
    return `/properties/${locationSlug}/${lastSegment}`;
}

/** True when the segment contains a UUID (legacy `{title-slug}-{uuid}` or bare id links). */
export function hasPropertyUuid(segment: string): boolean {
    return UUID_RE.test(segment || '');
}

/**
 * Extract the property UUID from a route segment that may be either a bare id
 * or a `{title-slug}-{id}` combination. Falls back to the raw segment.
 */
export function extractPropertyId(segment: string): string {
    const match = (segment || '').match(UUID_RE);
    return match ? match[0] : segment;
}
